import React from 'react';
import { 
  GitBranch, 
  FileCheck, 
  Users, 
  Scale, 
  Lightbulb, 
  CheckCircle,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../../Images/piping.webp";

const PipingDesignPage = () => {
  const services = [
    {
      title: "3D Piping Layout Design",
      description: "State-of-the-art 3D modeling and visualization for complex piping systems, ensuring optimal space utilization and accessibility.",
      icon: <GitBranch className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
    },
    {
      title: "Technical Specifications",
      description: "Detailed technical documentation including material specifications, stress analysis, and compliance requirements.",
      icon: <FileCheck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
    },
    {
      title: "Installation Support",
      description: "Expert guidance during installation phase with on-site supervision and quality control measures.",
      icon: <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
    }
  ];

  const processSteps = [
    {
      title: "Requirements Analysis",
      description: "In-depth assessment of your project needs and constraints"
    },
    {
      title: "Conceptual Design",
      description: "Initial design concepts and feasibility studies"
    },
    {
      title: "Detailed Engineering",
      description: "Comprehensive technical specifications and drawings"
    },
    {
      title: "Implementation Support",
      description: "Expert guidance during construction and installation"
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Piping Design & Consultation Services",
    "description": "Expert engineering solutions for industrial piping systems including 3D design, technical specifications, and installation support",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Piping Design Services",
      "itemListElement": services.map((service) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Industrial Piping Design & Consultation Services | 3D Layout & Engineering | Gortherm Energy</title>
        <meta name="description" content="Professional industrial piping design and consultation services. Expert 3D piping layout, technical specifications, stress analysis, and installation support. Comprehensive engineering solutions for complex piping systems." />
        <meta name="keywords" content="piping design, industrial piping, 3D piping layout, piping engineering, piping consultation, stress analysis, piping specifications, process piping, piping installation, piping design Nepal, industrial engineering" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Piping Design & Consultation Services | Gortherm Energy" />
        <meta property="og:description" content="Expert engineering solutions for industrial piping systems, from concept to completion. Professional 3D design and technical consultation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/piping-design" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Piping Design & Consultation | Gortherm Energy" />
        <meta name="twitter:description" content="Comprehensive piping design services with 3D modeling, technical specifications, and installation support." />
        <meta name="twitter:image" content={img} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/piping-design" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="w-full">
        {/* Hero Section */}
        <header className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r from-slate-600 to-blue-900 overflow-hidden pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img
              src={img}
              alt="Industrial piping design and engineering services by Gortherm Energy"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
              width="1920"
              height="400"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
                Piping Design & Consultation
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-6 sm:mb-8">
                Expert engineering solutions for industrial piping systems, from concept to completion.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a href="/contact">
                  <button className="bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-all text-sm sm:text-base font-semibold w-full sm:w-auto">
                    Request Consultation
                    <Users className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  </button>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Services Grid */}
          <section className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Our Design Services</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive piping design and consultation services backed by decades of 
              industry experience and cutting-edge technical expertise.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <article 
                key={index}
                className="p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </article>
            ))}
          </div>

          {/* Process Timeline */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Our Design Process</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <span className="text-blue-600 font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-blue-100"></div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Technical Expertise */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img 
                src={img}
                alt="Professional piping design and engineering technical drawings"
                className="rounded-lg shadow-xl object-cover h-full w-full"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Technical Excellence</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Industry Standards Compliance</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      All designs meet or exceed international piping standards including ASME B31.3, ASME B31.1, and local regulations.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Innovative Solutions</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Creative problem-solving approach to complex piping challenges with optimized layouts and cost-effective solutions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Quality Assurance</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Rigorous quality control processes ensuring accuracy, safety, and optimal performance throughout all project phases.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Capabilities Section */}
          <section className="bg-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Our Design Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-700">Process Piping</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Complete process piping design for chemical, petrochemical, and industrial facilities with stress analysis and material selection.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-700">Utility Systems</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Steam, condensate, compressed air, and water distribution systems optimized for efficiency and reliability.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-700">3D Modeling</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Advanced 3D CAD modeling for clash detection, space optimization, and visualization before construction.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-700">Documentation</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Comprehensive technical drawings, isometrics, material take-offs, and as-built documentation for project success.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <aside className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Start Your Piping Project?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Consult with our piping design experts to bring your project from concept to reality with precision engineering.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base lg:text-lg transition-all duration-300 shadow-md hover:shadow-xl w-full sm:w-auto">
                  Request Design Consultation
                </button>
              </a>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Contact piping design experts at +977 980 278 1400"
              >
                <button className="bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold w-full sm:w-auto justify-center">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span>Call Our Experts</span>
                </button>
              </a>
            </div>
            <p className="mt-4 text-sm sm:text-base text-gray-600">
              Call us at: <a href="tel:+9779802781400" className="text-blue-600 hover:text-blue-700 font-semibold">+977 980 278 1400</a>
            </p>
          </aside>
        </div>
      </main>
    </>
  );
};

export default PipingDesignPage;